import React, { useState, useEffect } from "react";
import NavBar from "@/components/navigation/NavBar";
import SuccessModal from "@/components/Motion/SuccessModal";
import FormWizard from "./components/FormWizard";
import Preview from "./components/Preview";
import { useFormData } from "@/stores";
import { useCoordinates } from "@/stores";
import { useTrackingFunctions } from "@/lib/meta/TrackingFunctions";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { MapIcon } from "@heroicons/react/24/outline";
import { useStepNavigation } from "./hooks/useStepNavigation";
import { useResetForm } from "@/stores";
import { useSearchParams } from "react-router-dom";
import type { StepId } from "./types";

/**
 * EvaluationTool - Main component for property evaluation form
 *
 * This is a refactored, production-ready version using TypeScript,
 * react-hook-form, Zod validation, and proper separation of concerns.
 */
const EvaluationTool: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [started, setStarted] = useState(false);
  const [formKey, setFormKey] = useState(0); // Key to force remount of form
  const [searchParams] = useSearchParams();

  const formData = useFormData();
  const coordinates = useCoordinates();
  const resetForm = useResetForm();

  // Determine initial step from URL query param or default to 'property-type'
  const stepParam = searchParams.get("step") as StepId | null;
  const initialStep: StepId = stepParam || "property-type";

  // Use separate hook for step navigation
  const { currentStepId, navigateToStep } = useStepNavigation({
    initialStep,
  });

  const handleFullReset = () => {
    if (confirm("Möchten Sie das Formular wirklich zurücksetzen?")) {
      resetForm(); // Reset store
      navigateToStep("property-type"); // Reset navigation
      setFormKey((prev) => prev + 1); // Force remount of FormWizard to clear RHF state
      window.scrollTo(0, 0);
    }
  };

  // Tracking
  let trackBewertungStart: (() => void) | undefined;
  let trackBewertungAbgeschlossen: ((url: string) => void) | undefined;

  try {
    const trackingFunctions = useTrackingFunctions();
    trackBewertungStart = trackingFunctions.trackBewertungStart;
    trackBewertungAbgeschlossen = trackingFunctions.trackBewertungAbgeschlossen;
  } catch (error) {
    console.warn("Failed to get tracking functions:", error);
  }

  // Track form start
  useEffect(() => {
    if (!started && trackBewertungStart) {
      try {
        trackBewertungStart();
        setStarted(true);
      } catch (error) {
        console.warn("Failed to track bewertung start:", error);
      }
    }
  }, [started, trackBewertungStart]);

  const handleSuccess = () => {
    setShowModal(true);
    if (trackBewertungAbgeschlossen) {
      try {
        trackBewertungAbgeschlossen(window.location.pathname);
      } catch (error) {
        console.warn("Failed to track bewertung completed:", error);
      }
    }

    // Reset form after a delay to allow modal to be seen/closed
    setTimeout(() => {
      resetForm();
      navigateToStep("property-type");
      setFormKey((prev) => prev + 1);
      setStarted(false);
    }, 2000);
  };

  const handleError = (error: Error) => {
    console.error("Submission error:", error);
    alert(`Fehler: ${error.message || "Bitte versuchen Sie es erneut."}`);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <SuccessModal modal={showModal} />
      <NavBar className="relative bg-white border-b border-gray-200 shrink-0" />

      <div className="flex-1 overflow-hidden max-w-[95rem] w-full mx-auto">
        <div className="h-full w-full grid grid-cols-1 lg:grid-cols-5 overflow-x-hidden">
          {/* Preview Section - Left Side (40%) */}
          <aside className="overflow-hidden col-span-2 hidden lg:w-auto lg:flex lg:flex-col bg-gray-100 border-r border-gray-300 h-full">
            <Preview
              formData={formData as any}
              coordinates={coordinates}
              currentStepId={currentStepId}
              onStepClick={navigateToStep}
              onReset={handleFullReset}
              className="flex-1"
            />
          </aside>

          {/* Form Section - Right Side (Main Focal Point, 60%) */}
          <main className="w-full lg:flex lg:items-start col-span-3 lg:justify-center bg-gray-50 p-2 sm:p-4 lg:p-8 h-full overflow-hidden overflow-x-hidden">
            <div className="w-full max-w-[600px] h-full max-h-full flex flex-col overflow-hidden overflow-x-hidden">
              {/* Mobile: Show preview button */}
              <Drawer>
                <DrawerTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full lg:hidden mb-3 bg-white text-prime-700 hover:bg-prime-50 border-prime-300"
                  >
                    <MapIcon className="size-5 mr-2" />
                    Vorschau anzeigen
                  </Button>
                </DrawerTrigger>
                <DrawerContent className="max-h-[85vh] p-0 flex flex-col">
                  <div className="flex-1 min-h-0 overflow-y-auto bg-gray-100">
                    <Preview
                      formData={formData as any}
                      coordinates={coordinates}
                      currentStepId={currentStepId}
                      onStepClick={navigateToStep}
                      onReset={handleFullReset}
                      className="w-full"
                    />
                  </div>
                </DrawerContent>
              </Drawer>

              {/* Form Wizard */}
              <FormWizard
                key={formKey}
                onSuccess={handleSuccess}
                onError={handleError}
                currentStepId={currentStepId}
                onStepChange={navigateToStep}
              />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default EvaluationTool;
